import { createContext, useCallback, useEffect, useMemo, useState } from 'react';

/**
 * ThemeContext — light/dark theming with persistence + system awareness.
 *
 * Three user preferences are supported:
 *   'light'  — always light
 *   'dark'   — always dark
 *   'system' — follow the OS color scheme (live-updates as the OS changes)
 *
 * The *resolved* theme ('light' | 'dark') is what actually renders; it is
 * applied by toggling the `dark` class on <html> so Tailwind's `dark:` variants
 * and the dark retrofit layer in index.css take effect across the whole app.
 *
 * Kept separate from auth so theming works on public pages too.
 */
export const ThemeContext = createContext(undefined);

const STORAGE_KEY = 'lx-theme';
const PREFERENCES = ['light', 'dark', 'system'];

/** Read the saved preference, defaulting to 'system' for new visitors. */
function readPreference() {
  if (typeof window === 'undefined') return 'system';
  try {
    const saved = window.localStorage.getItem(STORAGE_KEY);
    if (PREFERENCES.includes(saved)) return saved;
  } catch {
    /* localStorage unavailable (private mode / blocked) — fall through */
  }
  return 'system';
}

/** Does the OS currently prefer a dark scheme? */
function systemPrefersDark() {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-color-scheme: dark)').matches
  );
}

/** Resolve a preference into the concrete theme that renders. */
function resolveTheme(preference) {
  if (preference === 'system') return systemPrefersDark() ? 'dark' : 'light';
  return preference;
}

export function ThemeProvider({ children }) {
  const [preference, setPreferenceState] = useState(readPreference);
  const [theme, setTheme] = useState(() => resolveTheme(readPreference()));

  // Apply the resolved theme to <html> and keep it in sync with the preference
  // (and, when 'system', with live OS changes).
  useEffect(() => {
    const apply = () => {
      const next = resolveTheme(preference);
      setTheme(next);
      const root = document.documentElement;
      root.classList.toggle('dark', next === 'dark');
      root.style.colorScheme = next; // native form controls / scrollbars
    };

    apply();

    if (preference !== 'system' || typeof window.matchMedia !== 'function') {
      return undefined;
    }
    // Live-follow the OS while on 'system'.
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    mq.addEventListener('change', apply);
    return () => mq.removeEventListener('change', apply);
  }, [preference]);

  const setPreference = useCallback((next) => {
    if (!PREFERENCES.includes(next)) return;
    setPreferenceState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* ignore persistence failures */
    }
  }, []);

  // Flip directly between light and dark (escapes 'system').
  const toggleTheme = useCallback(() => {
    setPreference(resolveTheme(preference) === 'dark' ? 'light' : 'dark');
  }, [preference, setPreference]);

  const value = useMemo(
    () => ({
      theme, // resolved: 'light' | 'dark'
      preference, // 'light' | 'dark' | 'system'
      isDark: theme === 'dark',
      setPreference,
      setTheme: setPreference, // back-compat alias
      toggleTheme,
    }),
    [theme, preference, setPreference, toggleTheme]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}
